import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { FolderOpen, Upload, Download, Trash2, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/Shared';
import {
  getUsers, adminListEmployeeDocuments, adminUploadEmployeeDocument,
  adminDeleteEmployeeDocument, adminOpenEmployeeDocument,
} from '../../services/api';

const TYPES = [
  { value: 'payslip', label: 'Payslip' },
  { value: 'contract', label: 'Contract' },
  { value: 'hr', label: 'HR document' },
  { value: 'other', label: 'Other' },
];

export default function EmployeeDocuments() {
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [userId, setUserId] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [us, docs] = await Promise.all([
        users.length ? Promise.resolve(users) : getUsers(),
        adminListEmployeeDocuments(userId || undefined),
      ]);
      if (!users.length) setUsers(us.filter(u => ['employee', 'subcontractor', 'admin'].includes(u.role)));
      setRows(docs);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const grouped = useMemo(() => {
    const byUser = {};
    for (const d of rows) {
      byUser[d.userId] = byUser[d.userId] || { user: { id: d.userId, name: d.userName }, docs: [] };
      byUser[d.userId].docs.push(d);
    }
    return Object.values(byUser).sort((a, b) => (a.user.name || '').localeCompare(b.user.name || ''));
  }, [rows]);

  const del = async (d) => {
    if (!window.confirm(`Delete "${d.title}" for ${d.userName}?`)) return;
    try { await adminDeleteEmployeeDocument(d.id); toast.success('Document archived'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="admin-employee-documents">
      <PageHeader
        title="Employee Documents"
        description="Private payslips, contracts and HR docs — upload per engineer."
        actions={<Button onClick={() => setShowUpload(true)} data-testid="admin-docs-upload-btn">
          <Upload className="h-4 w-4 mr-1" />Upload
        </Button>}
      />

      <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3">
        <label className="text-xs text-slate-600 flex items-center gap-2">
          Employee
          <select className="border rounded px-2 py-1 text-sm" value={userId}
            onChange={(e) => setUserId(e.target.value)}
            data-testid="admin-docs-filter-user">
            <option value="">All</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </label>
      </div>

      <div className="space-y-4">
        {loading && <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>}
        {!loading && grouped.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm bg-white border border-slate-200 rounded-lg">
            No documents uploaded yet.
          </div>
        )}
        {grouped.map((g) => (
          <div key={g.user.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`admin-docs-group-${g.user.id}`}>
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 font-medium text-sm">{g.user.name}</div>
            <div className="divide-y divide-slate-100">
              {g.docs.map((d, i) => (
                <div key={d.id} className="p-3 flex items-center gap-3" data-testid={`admin-docs-row-${d.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 truncate">{d.title}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {d.type}{d.period ? ' · ' + d.period : ''} · {(d.createdAt || '').slice(0, 10)}
                      {d.requiresAcknowledgement && (d.acknowledgedAt
                        ? <span className="ml-2 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Acknowledged</span>
                        : <span className="ml-2 text-amber-700">Awaiting acknowledgement</span>)}
                    </div>
                  </div>
                  <button onClick={() => adminOpenEmployeeDocument(d.id, d.fileName)}
                    className="p-2 rounded hover:bg-slate-100 text-slate-700" title="Download"
                    data-testid={`admin-docs-open-${d.id}`}>
                    <Download className="h-4 w-4" />
                  </button>
                  <button onClick={() => del(d)} className="p-2 rounded hover:bg-rose-50 text-rose-600" title="Delete"
                    data-testid={`admin-docs-del-${d.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showUpload && (
        <UploadDialog users={users} defaultUserId={userId}
          onClose={() => setShowUpload(false)}
          onSaved={async () => { setShowUpload(false); await load(); }} />
      )}
    </div>
  );
}

function UploadDialog({ users, defaultUserId, onClose, onSaved }) {
  const [form, setForm] = useState({
    userId: defaultUserId || '', docType: 'payslip', title: '', period: '', requiresAcknowledgement: false,
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.userId) { toast.error('Choose an employee'); return; }
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!file) { toast.error('Choose a PDF file'); return; }
    setBusy(true);
    try {
      await adminUploadEmployeeDocument(form, file);
      toast.success('Document uploaded');
      await onSaved();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Upload failed');
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-white w-full sm:max-w-lg sm:rounded-lg p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
        data-testid="admin-docs-upload-form">
        <h2 className="font-display text-lg font-bold mb-3">Upload document</h2>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Employee *</span>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.userId} onChange={update('userId')}
              data-testid="admin-docs-upload-user">
              <option value="">Select…</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Type *</span>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.docType} onChange={update('docType')}
              data-testid="admin-docs-upload-type">
              {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Title *</span>
            <input className="w-full border rounded px-2 py-1.5 text-sm" value={form.title} onChange={update('title')}
              data-testid="admin-docs-upload-title" placeholder="e.g. February 2026 Payslip" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">Period</span>
            <input className="w-full border rounded px-2 py-1.5 text-sm" value={form.period} onChange={update('period')}
              placeholder="e.g. Feb 2026" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.requiresAcknowledgement} onChange={update('requiresAcknowledgement')}
              data-testid="admin-docs-upload-ack" />
            Require acknowledgement
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">File (PDF) *</span>
            <input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm" data-testid="admin-docs-upload-file" />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
          <Button type="submit" disabled={busy} data-testid="admin-docs-upload-submit">
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      </form>
    </div>
  );
}
